import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useConfirmation } from 'app/components/ConfirmationContext'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUserStore } from 'app/stores/StoreContext'
import type { DeleteUserMutation } from 'app/generated/gql/graphql'

export type DeleteUserOptions = {
  /** Confirmation dialog body. Defaults to a generic delete warning. */
  confirmDescription?: string
  /** Path to navigate to after a successful delete. Defaults to the users list. */
  redirectTo?: string
}

const DEFAULT_CONFIRM_DESCRIPTION = `Are you sure you want to delete this user? You will not have a chance to undo this action.`
const DEFAULT_REDIRECT = `/settings/users`

/**
 * Encapsulates the "delete a user" flow: confirm, run the `deleteUser` mutation,
 * toast the outcome, and navigate away on success. Callers read `isDeleting`
 * instead of owning their own loading flag.
 *
 * The in-flight state is a manual flag, not `useTransition`/`useActionState`,
 * and this stays true under React 19. Two properties of this flow rule the
 * Actions APIs out:
 *   - `deleteUser` returns an awaitable `Promise<void>` that callers depend on
 *     (`useUserDetails` re-exposes it as `deleteUserHandler: () => Promise<void>`).
 *     `startTransition` returns `void`, not a promise that settles with its
 *     async action, so moving the await inside it would resolve `deleteUser`
 *     before the delete finished.
 *   - The flag must flip on only AFTER the confirm dialog resolves, never while
 *     the user is still deciding. A transition wrapping the whole flow would
 *     report pending during the confirm wait.
 * `useActionState` is form-action shaped (`[state, formAction, isPending]`) and
 * does not fit a hook that takes a `userId` argument and returns a promise.
 *
 * The API `deleteUser` mutation returns `Boolean!`; a truthy result means the user
 * was deleted.
 */
export function useDeleteUser() {
  const userStore = useUserStore()
  const navigate = useNavigate()
  const [confirm] = useConfirmation()
  const [isDeleting, setIsDeleting] = useState(false)

  const deleteUser = async (userId: string, options: DeleteUserOptions = {}) => {
    const { confirmDescription = DEFAULT_CONFIRM_DESCRIPTION, redirectTo = DEFAULT_REDIRECT } = options

    // `confirm` rejects when the user declines; treat that as a no-op and never
    // enter the deleting state.
    try {
      await confirm({ title: `Delete User`, description: confirmDescription, variant: `danger` })
      // oxlint-disable-next-line local/require-catch-logging -- declined confirmation is an intentional no-op
    } catch {
      return
    }

    setIsDeleting(true)
    try {
      const result = await userStore.remove(userId)
      if (result.error) {
        notifyError(result.error.message)
        return
      }
      if ((result.data as DeleteUserMutation | undefined)?.deleteUser) {
        notifySuccess(`User deleted`)
        navigate(redirectTo)
      }
    } finally {
      setIsDeleting(false)
    }
  }

  return { deleteUser, isDeleting }
}
